// =============================================================================
// useFormsTemplates — TanStack Query hooks for Clinio Forms template library
// =============================================================================
// Linear: CLINIO-217 (FORMS-6)
//
// Wraps the two v2 Edge Functions:
//   * forms-list-templates  → useFormsTemplateList
//   * forms-get-template    → useFormsTemplateDetail
//
// Both use a 5-minute stale window per spec.
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toFormsLocale } from '@/lib/questionnaireDisplayName';
import { normalizeQuestionnaireTags } from '@/lib/questionnaireTags';
import { formsEdgeHeaders, formsEdgeUrl } from '@/lib/formsEdge';

// ---------------------------------------------------------------------------
// Shared types (mirrored from Edge Function output shapes)
// ---------------------------------------------------------------------------

export type FormsTemplateSummary = {
  id: string;
  questionnaire_id?: string;
  template_id?: string;
  display_name_da: string;
  display_name_en: string;
  description: string | null;
  category: string | null;
  tags: string[];
  question_count: number | null;
  estimated_minutes: number | null;
  version: string;
  is_platform: boolean;
  /** null = clinic-authored. 'stub' | 'content_pending_license' | 'ready' for platform rows */
  seed_status: 'stub' | 'content_pending_license' | 'ready' | null;
};

export type FormsTemplateLibrary = {
  platform: FormsTemplateSummary[];
  clinic: FormsTemplateSummary[];
};

export type FormsTemplateDetail = FormsTemplateSummary & {
  json_schema_preview: Record<string, unknown> | null;
};

// ---------------------------------------------------------------------------
// Internal: Edge Function callers
// supabase-js v2 invoke() doesn't support URL query params for GET requests,
// so we use a direct fetch with the session token instead.
// ---------------------------------------------------------------------------

async function callTemplateListEF(
  clinicId: string,
  locale?: string,
): Promise<FormsTemplateLibrary> {
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) throw new Error('Not authenticated');

  const formsLocale = toFormsLocale(locale);
  const query: Record<string, string> = { clinic_id: clinicId };
  if (formsLocale) query.locale = formsLocale;

  const resp = await fetch(formsEdgeUrl('forms-list-templates', query), {
    method: 'GET',
    headers: formsEdgeHeaders(token),
  });

  const json: { ok: boolean; data?: FormsTemplateLibrary; error?: unknown } = await resp.json();
  if (!json.ok || !json.data) {
    throw new Error('forms-list-templates returned error');
  }
  return {
    platform: json.data.platform.map(normalizeTemplateSummary),
    clinic: json.data.clinic.map(normalizeTemplateSummary),
  };
}

function normalizeTemplateSummary(row: FormsTemplateSummary): FormsTemplateSummary {
  return {
    ...row,
    tags: normalizeQuestionnaireTags(row.tags),
    question_count: typeof row.question_count === 'number' && Number.isFinite(row.question_count)
      ? row.question_count
      : null,
    description: row.description ?? null,
    category: row.category ?? null,
  };
}

export async function fetchFormsTemplateDetail(
  clinicId: string,
  templateId: string,
): Promise<FormsTemplateDetail> {
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) throw new Error('Not authenticated');

  const resp = await fetch(
    formsEdgeUrl('forms-get-template', { clinic_id: clinicId, template_id: templateId }),
    {
      method: 'GET',
      headers: formsEdgeHeaders(token),
    },
  );

  const json: { ok: boolean; data?: FormsTemplateDetail; error?: unknown } = await resp.json();
  if (!json.ok || !json.data) {
    throw new Error('forms-get-template returned error');
  }
  return { ...json.data, ...normalizeTemplateSummary(json.data) };
}

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

/** 5-minute cache per spec (FORMS-6). Returns platform + clinic template groups. */
export function useFormsTemplateList(
  clinicId: string | null | undefined,
  locale?: string,
) {
  const formsLocale = toFormsLocale(locale);
  return useQuery<FormsTemplateLibrary, Error>({
    queryKey: ['forms', 'templates', 'list', clinicId ?? null, formsLocale ?? null],
    enabled: Boolean(clinicId),
    staleTime: 5 * 60 * 1000, // 5 minutes per FORMS-6 spec
    queryFn: () => callTemplateListEF(clinicId!, formsLocale),
  });
}

/** 5-minute cache for a single template detail (used in preview modal). */
export function useFormsTemplateDetail(
  clinicId: string | null | undefined,
  templateId: string | null | undefined,
) {
  return useQuery<FormsTemplateDetail, Error>({
    queryKey: ['forms', 'templates', 'detail', clinicId ?? null, templateId ?? null],
    enabled: Boolean(clinicId) && Boolean(templateId),
    staleTime: 5 * 60 * 1000,
    queryFn: () => fetchFormsTemplateDetail(clinicId!, templateId!),
  });
}

